import { useFormik } from "formik";
import { useSession } from "hooks/useSession";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { api } from "services/api";
import { numberToHex } from "utils/conversions";
import { getAverageRgb } from "utils/getAverageRgb";
import { imageToBase64 } from "utils/imageToBase64";
import { checkChange } from "utils/checkChange";
import { useAlert } from "hooks/useAlert";
import { useToast } from "hooks/useToast";
import * as Yup from 'yup'
import SellerDetailsContext from "..";
import { useAuth } from "hooks/useAuth";

const defaultHex = '#666bff';

interface ParamsProps {
  id: string;
}

interface Preference {
  sellerId?: number,
  nickname?: string,
  primaryColor?: string,
  logo?: string,
}

const initIdentityInputs: Preference = {
  nickname: '',
  primaryColor: defaultHex,
}

const translatedFields = (props: string) => (
  {
    "logo": "Logo",
    "nickname": "Nome Display",
    "primaryColor": "Cor Primária",
  }
)[props]; 

export const useIdentity = () => {
  const { id } = useParams<ParamsProps>();
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [imagePath, setImagePath] = useState<string>();
  const [initialInputsValues, setInitialInputsValues] = useState(initIdentityInputs);
  const [changedFields, setChangedFields] = useState<string[]>([])
  const { alert, setTextAlertInterface, openAlert, closeAlert, caller } = useAlert();
  const { setToast } = useToast();
  const {getSessionAuthorization} = useSession();
  const {setHasChangedFields} = useContext(SellerDetailsContext);
  const { connectApi, connectAllApi } = useAuth();
  const [logoIsPNG, setLogoIsPNG] = useState(false);
  const {handleSubmit, handleChange, setValues: setInputsValues, values: inputsValues, touched, errors} = useFormik({
    initialValues: initIdentityInputs,
    validationSchema: Yup.object({
      logo: Yup.string(),
      nickname: Yup.string(),
      primaryColor: Yup.string().matches(/#[a-fA-F0-9]{6}/),
    }),
    onSubmit: (values) => {
      submitData(values);
    }
  });

  /* HOOKS */
  useEffect(() => {
    getData();
  }, [])

  useEffect(() => {
    const changed = checkChange(inputsValues, initialInputsValues, true);
    setHasChangedFields(changed.length > 0)
    setChangedFields(changed);
  }, [inputsValues])

  useEffect(() => {
    if (caller === 'identity') {
      alert.typeContinue === "save" && saveFormChanges();
      alert.typeContinue === "discard" && discardChanges();
    }
  }, [alert])

  /* FORM SUBMISSION HANDLERS */
  const saveFormChanges = () => {
    handleSubmit();
    if (Object.keys(errors).length > 0) {
      setToast({
        message: `Favor corrigir o(s) campo(s) indicado(s)`,
        isActive: true,
        type: 'error',
        timeToRemoveToast: 3000
      })
      closeAlert();
      return;
    }
  }
  
  const discardChanges = () => {
    setInputsValues(initialInputsValues);
    setImagePath(undefined);
    closeAlert();
  }

  /* EVENT HANDLERS */
  const imageSelectedHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target?.files) {
      const imageUrl = window.URL.createObjectURL(event.target.files[0]);
      const imageFile = event.target.files[0];
      if(imageFile.type === "image/png"){
        setLogoIsPNG(true)
        if (imageFile)
          imageToBase64(imageFile).then((result) => {
            getAverageRgb(result).then((rgb) => {
              let hex = `#${numberToHex(rgb.r, 2)}${numberToHex(rgb.g, 2)}${numberToHex(rgb.b, 2)}`
              setInputsValues({
                ...inputsValues,
                logo: result,
                primaryColor: hex,
              })
            })
          })
        setImagePath(imageUrl);
      }else{
        setLogoIsPNG(false)
        setImagePath('dont PNG');
      }
    }
  }

  const handleFormDiscard = () => {
    showAlertForm(false)
  }

  const handleFormSubmit = () => {
    showAlertForm(true)
  }

  /* HELPERS */
  const showAlertForm = (typeOpen: boolean) => {
    setTextAlertInterface({
      positionCenter: false,
    })
    openAlert(typeOpen, 'identity');
  }

  const textColor = ():'white' | 'black' => {
    const hex = inputsValues.primaryColor?.replace(/[^a-zA-Z0-9]/, "") ?? defaultHex;
    const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    const channels = rgb ? {
      r: parseInt(rgb[1], 16),
      g: parseInt(rgb[2], 16),
      b: parseInt(rgb[3], 16)
    } : null;

    if (channels) {
      // Color brightness is determined by the following formula:
      // ((Red value X 299) + (Green value X 587) + (Blue value X 114)) / 1000
      const brightness = Math.round(((channels.r * 299) +
                                     (channels.g * 587) +
                                     (channels.b * 114)) / 1000);
      const textColor = (brightness > 125) ? 'black' : 'white';
      return textColor;
    }

    // default to black
    return 'black';
  }

  /* API CALLS */
  const getData = () => {

    setLoading(true);
    connectApi({
      type: 'get',
      url: `sellers/${id}/preference`,
    }).then((response) => {
      setHasError(false);
      let responseDecoded: Preference = response.data;
      let responseObject = {
        logo: responseDecoded.logo ?? initIdentityInputs.logo,
        nickname: responseDecoded.nickname ?? initIdentityInputs.nickname,
        primaryColor: responseDecoded.primaryColor ?? initIdentityInputs.primaryColor,
      }
      setInitialInputsValues(responseObject);
      setInputsValues(responseObject);
    }).catch((_) => {
      setHasError(true);
    }).finally(() => {
      setLoading(false);
    })
  }

  const submitData = async (values: Preference) => {
    const preferenceBody = {
      nickname: values.nickname,
      primaryColor: values.primaryColor,
    };
    const logoBody = values.logo?.split('base64,')[1];

    connectAllApi([
      {type: 'put', url: `sellers/${id}/preference`, body: preferenceBody},
      {type: 'put', url: `sellers/${id}/logo`, body: {logo: logoBody}}
    ]).then((responses) => {
      if (responses[0].status !== 'rejected' || responses[1].status !== 'rejected') {
        setToast({
          message: `Seller atualizado com sucesso`,
          isActive: true,
          type: 'success',
          timeToRemoveToast: 3000
        });
        getData();
        closeAlert();
      }
    }).catch((_) => closeAlert());
  }

  return {
    loading,
    hasError,
    imagePath,
    inputsValues,
    changedFields,
    touched,
    errors,
    imageSelectedHandler,
    handleChange,
    textColor,
    translatedFields,
    handleFormSubmit,
    handleFormDiscard,
    logoIsPNG
  }
}